import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage, fmtDate } from "@/lib/api";
import { KeyFormDialog } from "@/components/KeyFormDialog";
import { NewKeyDialog } from "@/components/NewKeyDialog";
import { BlockedIps } from "@/components/BlockedIps";

const keyState = (k) => (!k.active ? "revoked" : k.expires_at && k.expires_at < new Date().toISOString() ? "expired" : "active");
const STATE = { active: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", revoked: "text-red-400 border-red-500/30 bg-red-500/10", expired: "text-amber-400 border-amber-500/30 bg-amber-500/10" };

export default function ApiKeys() {
  const qc = useQueryClient();
  const { data: keys = [], isLoading } = useQuery({ queryKey: ["api-keys"], queryFn: () => api.get("/admin/api-keys").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [created, setCreated] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["api-keys"] });

  const save = async (body) => {
    setBusy(true);
    try {
      if (form.key) { await api.patch(`/admin/api-keys/${form.key.id}`, body); toast.success("Key updated"); }
      else { const { data } = await api.post("/admin/api-keys", body); setCreated(data); }
      setForm(null); refresh();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  const revoke = async () => {
    try { await api.delete(`/admin/api-keys/${revoking.id}`); toast.success(`Revoked “${revoking.name}”`); refresh(); }
    catch (e) { toast.error(errorMessage(e)); }
    setRevoking(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 rise">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-indigo-400">Access control</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">API Keys</h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">Who can call the chatbot API, and how much.</p>
        </div>
        <Button data-testid="create-api-key-button" onClick={() => setForm({})} className="bg-indigo-500 hover:bg-indigo-600 active:scale-95 transition-transform"><Plus className="h-4 w-4 mr-1" />New key</Button>
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-xl overflow-hidden rise" style={{ animationDelay: "80ms" }}>
        <div className="overflow-x-auto">
          <Table data-testid="api-keys-table">
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {["Name", "Key", "Status", "Today", "Limits", "Allowed IPs", "Expires", "Last used", ""].map((h) => (
                  <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => {
                const st = keyState(k);
                return (
                  <TableRow key={k.id} data-testid={`api-key-row-${k.id}`} className={`border-slate-800 hover:bg-slate-800/40 transition-colors ${st !== "active" ? "opacity-60" : ""}`}>
                    <TableCell className="text-sm text-slate-100 whitespace-nowrap">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400">{k.key_prefix}…</TableCell>
                    <TableCell><span data-testid="api-key-status" className={`px-2 py-0.5 rounded border font-mono text-[11px] uppercase ${STATE[st]}`}>{st}</span></TableCell>
                    <TableCell className="font-mono text-sm whitespace-nowrap">{k.questions_today}<span className="text-slate-600">/{k.effective_per_day}</span></TableCell>
                    <TableCell className="font-mono text-xs text-slate-400 whitespace-nowrap">{k.effective_per_minute}/min · {k.effective_per_day}/day</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400 max-w-[180px] break-all">{k.allowed_ips?.length ? k.allowed_ips.join(", ") : <span className="text-slate-600">any</span>}</TableCell>
                    <TableCell className="text-xs text-slate-400 whitespace-nowrap">{k.expires_at ? fmtDate(k.expires_at) : <span className="text-slate-600">never</span>}</TableCell>
                    <TableCell className="text-xs text-slate-400 whitespace-nowrap">{k.last_used_at ? <>{fmtDate(k.last_used_at)}<br /><span className="font-mono text-slate-500">{k.last_used_ip}</span></> : <span className="text-slate-600">never</span>}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {k.active && <>
                        <Button data-testid="edit-api-key-button" size="icon" variant="ghost" onClick={() => setForm({ key: k })} className="text-slate-400 hover:text-white hover:bg-slate-800"><Pencil className="h-4 w-4" /></Button>
                        <Button data-testid="revoke-api-key-button" size="icon" variant="ghost" onClick={() => setRevoking(k)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"><Ban className="h-4 w-4" /></Button>
                      </>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!keys.length && <TableRow><TableCell colSpan={9} data-testid="api-keys-empty" className="text-center py-12 text-slate-500 text-sm">{isLoading ? "Loading…" : "No API keys yet."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <BlockedIps />

      <KeyFormDialog open={!!form} initial={form?.key} busy={busy} onClose={() => setForm(null)} onSubmit={save} />
      <NewKeyDialog created={created} onClose={() => setCreated(null)} />
      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent data-testid="revoke-confirm-dialog" className="bg-[#0F172A] border-slate-700 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Revoke “{revoking?.name}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Any system using this key will immediately get 401 errors. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-cancel-button" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="revoke-confirm-button" onClick={revoke} className="bg-red-500 hover:bg-red-600">Revoke key</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
